import { zodResolver } from "@hookform/resolvers/zod";
import { ClockPlus, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { DatePicker } from "@/shared/components/DatePicker";
import {
  FormDialog,
  submitOnEnter,
  useCloseOrError,
} from "@/shared/components/FormDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Textarea } from "@/shared/components/ui/textarea";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import { todayIso } from "@/shared/utils/date";
import {
  useCreateAbsence,
  useDeleteAbsence,
  useUpdateAbsence,
} from "../api/attendance.mutations";
import {
  type AbsenceFormInput,
  type AbsenceFormValues,
  absenceSchema,
} from "../attendance.schemas";
import type { AbsenceDateRange } from "../attendance.types";
import type { CellAbsence } from "./useAbsenceTooltip";

// New absences default to today (not the window start, which may be past); edits keep their dates.
// todayIso() is per-call so a re-seed picks up the current day.
function seedValues(
  absence?: CellAbsence,
  fallbackRange?: AbsenceDateRange,
): AbsenceFormInput {
  const startDate = fallbackRange?.startDate ?? todayIso();
  return {
    startDate: absence?.startDate ?? startDate,
    endDate: absence?.endDate ?? fallbackRange?.endDate ?? startDate,
    reason: absence?.reason ?? "",
  };
}

export function AbsenceDialog(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  // Present → edit; absent → create (mode derived from this).
  absence?: CellAbsence;
  // Create mode: preselect this range; defaults to today.
  defaultRange?: AbsenceDateRange;
}) {
  const { open, onOpenChange, absence, defaultRange } = props;
  const mode = absence ? "edit" : "create";

  const form = useForm<AbsenceFormInput, unknown, AbsenceFormValues>({
    resolver: zodResolver(absenceSchema),
    defaultValues: seedValues(absence, defaultRange),
  });

  useResetOnOpen(open, () => form.reset(seedValues(absence, defaultRange)));

  const create = useCreateAbsence();
  const update = useUpdateAbsence();
  const del = useDeleteAbsence();
  const closeOrError = useCloseOrError(onOpenChange);

  const onSubmit = (values: AbsenceFormValues) => {
    if (mode === "create") {
      create.mutate(values, closeOrError("Kunde inte registrera frånvaro"));
    } else if (absence) {
      update.mutate(
        { id: absence.id, ...values },
        closeOrError("Kunde inte uppdatera frånvaro"),
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "create" ? "Ny frånvaro" : "Redigera frånvaro"}
      form={form}
      onSubmit={onSubmit}
      submitting={create.isPending || update.isPending}
      submitLabel={mode === "create" ? "Skapa" : "Spara"}
      submitIcon={mode === "create" ? <ClockPlus /> : <Save />}
      onDelete={
        absence
          ? () =>
              del.mutate(
                absence.id,
                closeOrError("Kunde inte ta bort frånvaro"),
              )
          : undefined
      }
      deleting={del.isPending}
    >
      <div className="flex items-start gap-3">
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Start</FormLabel>
              <DatePicker value={field.value} onChange={field.onChange} />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Slut</FormLabel>
              <DatePicker value={field.value} onChange={field.onChange} />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="reason"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Anledning</FormLabel>
            <FormControl>
              <Textarea
                maxLength={500}
                className="resize-none"
                {...field}
                onKeyDown={submitOnEnter(form.handleSubmit(onSubmit))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormDialog>
  );
}
